import {useCallback, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import useAuthentication from "../useAuthentication";
import useFirebase from "../../../util/useFirebase";

export const ACTION_RESET = "resetPassword";
export const ACTION_VERIFY_EMAIL = "verifyEmail";
export const ACTION_RECOVER_EMAIL = "recoverEmail";
export const ACTION_INVALID = "invalid";

const useAuthenticationAction = () => {
  const [queryParams] = useSearchParams();

  const {t} = useAuthentication();

  const {applyActionCode, checkActionCode, confirmPasswordReset, getAuth, verifyPasswordResetCode} = useFirebase();

  const navigate = useNavigate();

  const [action, setAction] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const updatePassword = useCallback(e => setPassword(e.target.value), []);
  const updatePasswordConfirm = useCallback(e => setPasswordConfirm(e.target.value), []);

  const mode = queryParams.get("mode");
  const oobCode = queryParams.get("oobCode");

  const auth = getAuth();

  const handleReset = useCallback(e => {
    e.preventDefault();

    if (password.length < 8 || !/\d+/.test(password) || !/[a-zA-Z]+/.test(password)) {
      return setError(t("error_password_invalid"));
    } else if (password !== passwordConfirm) {
      return setError(t("error_password_confirmation_must_match"));
    }

    setResetting(true);

    confirmPasswordReset(auth, oobCode, password)
      .then(() => navigate("/signin?reset=1"))
      .catch(e => {
        setResetting(false);

        console.error(e);
      });
  }, [t, password, passwordConfirm, navigate, oobCode, auth, confirmPasswordReset]);

  useEffect(() => {
    const splash = document.getElementById("splash");

    if (splash) {
      splash.remove();
    }

    if (mode === ACTION_RESET) {
      verifyPasswordResetCode(auth, oobCode)
        .then(email => {
          setEmail(email);
          setAction(ACTION_RESET);
        })
        .catch(() => setAction(ACTION_INVALID))
    } else if (mode === ACTION_VERIFY_EMAIL) {
      applyActionCode(auth, oobCode)
        .then(() => setAction(ACTION_VERIFY_EMAIL))
        .catch(() => setAction(ACTION_INVALID));
    } else if (mode === ACTION_RECOVER_EMAIL) {
      checkActionCode(auth, oobCode)
        .then(() => applyActionCode(auth, oobCode))
        .then(() => setAction(ACTION_RECOVER_EMAIL))
        .catch(() => setAction(ACTION_INVALID));
    }
  }, [mode, oobCode, auth, applyActionCode, checkActionCode, verifyPasswordResetCode]);

  return {
    t,
    resetting,
    handleReset,
    action,
    error,
    email,
    password,
    passwordConfirm,
    updatePassword,
    updatePasswordConfirm
  };
}

export default useAuthenticationAction;
